import { FunctionComponent, memo } from "react";
import { SpinnerUntil } from "@edgetier/components";
import { getTimeToRead, trimObjectValues } from "@edgetier/utilities";
import Axios from "axios";

import { useUser } from "hooks-for/users/use-user";
import UserForm from "components-for/users/user-form";

import { IFormValues, IProps } from "./invite-user-form.types";
import { axios } from "utilities/axios";
import { IUserPostResponse, Route } from "@edgetier/watchtower-types";
import toast from "react-hot-toast";
import { getServerError } from "utilities/get-server-error";
import { useQueryClient } from "react-query";
import { useTimeZones } from "hooks-for/time-zones/use-time-zone";

/**
 * A form for inviting a new user to Watchtower.
 * @param props.onInviteUser An optional callback that will run when a user is successfully invited.
 */
const InviteUserForm: FunctionComponent<IProps> = ({ onInviteUser }) => {
    const queryClient = useQueryClient();
    // Use the current user's language as the default selected language in the form.
    const { data: languageIsoCode, isLoading: isLanguageIsoCodeLoading } = useUser({
        select: ({ languageIsoCode }) => languageIsoCode,
    });

    // Select the time zone based on user's browser
    const { data: timeZone, isLoading: isTimeZoneLoading } = useTimeZones(
        {},
        {
            select: (timeZoneValues) =>
                timeZoneValues.find(
                    (timeZone) => timeZone.timeZone === Intl.DateTimeFormat().resolvedOptions().timeZone
                ),
        }
    );

    /**
     * Request that a user be invited to Watchtower.
     * @param values The details of the user to be invited.
     * @param errorCallback Optional callback action trigged on error.
     */
    const onSubmit = async (values: IFormValues, errorCallback?: VoidFunction) => {
        const trimmedValues = trimObjectValues(values);
        try {
            await axios.post<IUserPostResponse>(Route.Users, trimmedValues);
            queryClient.invalidateQueries(Route.Users);
            const successMessage = `Successfully invited ${values.firstName} to WatchTower`;
            toast.success(successMessage, { duration: getTimeToRead(successMessage, 1000) });
            onInviteUser();
        } catch (error) {
            if (Axios.isAxiosError(error)) {
                toast.error(getServerError(error));
            } else {
                toast.error("Failed to invite user to WatchTower.");
            }

            if (typeof errorCallback === "function") {
                errorCallback();
            }
        }
    };

    return (
        <SpinnerUntil data={languageIsoCode} isReady={!isLanguageIsoCodeLoading && !isTimeZoneLoading}>
            <UserForm
                initialValues={{
                    alertsEnabled: false,
                    firstName: "",
                    surname: "",
                    emailAddress: "",
                    languageIsoCode: languageIsoCode ?? "",
                    modelIds: [],
                    roleId: "",
                    timeZoneId: typeof timeZone !== "undefined" ? timeZone.timeZoneId : null,
                }}
                onSubmit={onSubmit}
            />
        </SpinnerUntil>
    );
};

export default memo(InviteUserForm);
